import React, { useState } from 'react';
import { Button, Table, Space, Modal, Tag, Tooltip, message, Empty, Tabs } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, FileTextOutlined, BookOutlined } from '@ant-design/icons';
import TemplateForm from './TemplateForm';
import apiClient, { API_BASE } from '../api';

const TemplateManager = ({ templates, functions, loading, onUpdate }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [viewingTemplate, setViewingTemplate] = useState(null);

  const handleCreate = () => {
    setEditingTemplate(null);
    setIsModalVisible(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setIsModalVisible(true);
  };

  const handleView = (template) => {
    setViewingTemplate(template);
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDelete = (id) => {
    setDeleteTarget(id);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const response = await apiClient.delete(`/templates/${deleteTarget}`);
      if (response.data.success) {
        message.success('Template deleted successfully');
        setDeleteTarget(null);
        onUpdate();
      } else {
        message.error(response.data.message || 'Failed to delete template');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to delete template');
      console.error(error);
    }
  };

  const handleFormSubmit = async (values) => {
    try {
      let response;
      if (editingTemplate) {
        response = await apiClient.put(`/templates/${editingTemplate._id}`, values);
      } else {
        response = await apiClient.post('/templates', values);
      }

      if (response.data.success) {
        message.success(`Template ${editingTemplate ? 'updated' : 'created'} successfully`);
        setIsModalVisible(false);
        onUpdate();
      } else {
        message.error(response.data.message || 'Operation failed');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Operation failed');
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'Template ID',
      dataIndex: 'templateId',
      key: 'templateId',
      width: 200,
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: 'Agent Name',
      dataIndex: 'agentName',
      key: 'agentName',
      width: 250,
      render: (text, record) => (
        <span>
          <strong>{text}</strong>
          {record.isDefault && <Tag color="gold" style={{ marginLeft: 8, fontSize: 10 }}>DEFAULT</Tag>}
        </span>
      ),
    },
    {
      title: 'System Prompt',
      dataIndex: 'systemPrompt',
      key: 'systemPrompt',
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span>{text.substring(0, 80)}...</span>
        </Tooltip>
      ),
    },
    {
      title: 'Knowledge',
      dataIndex: 'linkedFileId',
      key: 'linkedFileId',
      width: 150,
      render: (fileId) => (
        fileId 
          ? <Tag color="purple" icon={<BookOutlined />}>Linked</Tag>
          : <Tag color="default">None</Tag>
      ),
    },
    {
      title: 'Functions',
      dataIndex: 'enabledFunctions',
      key: 'enabledFunctions',
      width: 120,
      render: (functions) => (
        <Tag color="green">{functions?.length || 0} functions</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      fixed: 'right',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button 
            type="text" 
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
            title="View Details"
          />
          <Button 
            type="text" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ color: '#1890ff' }}
            title="Edit"
          />
          <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record._id)}
            title="Delete"
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0 }}>📋 Agent Templates</h2>
          <p style={{ color: '#666', margin: '4px 0 0 0' }}>
            Create reusable agent configurations with custom prompts and functions
          </p>
        </div>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          size="large"
          onClick={handleCreate}
        >
          Create Template
        </Button>
      </div>

      <Table 
        columns={columns} 
        dataSource={templates}
        rowKey="templateId"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 1000 }}
        locale={{
          emptyText: (
            <Empty 
              description="No templates yet. Create your first agent template!"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )
        }}
      />

      {/* Delete confirmation modal */}
      <Modal
        title="Delete Agent Template"
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onOk={handleConfirmDelete}
        okText="Delete"
        okButtonProps={{ danger: true }}
        destroyOnClose
      >
        <p>Are you sure you want to delete this template? This action cannot be undone.</p>
        {deleteTarget && (
          <p>
            <strong>Template ID:</strong>{' '}
            <Tag color="blue" style={{ marginLeft: 4 }}>
              {deleteTarget}
            </Tag>
          </p>
        )}
      </Modal>

      <Modal
        title={editingTemplate ? '✏️ Edit Template' : '➕ Create New Template'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={850}
        destroyOnClose
      >
        <TemplateForm
          initialValues={editingTemplate}
          functions={functions}
          onSubmit={handleFormSubmit}
          onCancel={() => setIsModalVisible(false)}
        />
      </Modal>

      <Modal
        title="👀 Template Details"
        open={!!viewingTemplate}
        onCancel={() => setViewingTemplate(null)}
        footer={[
          <Button key="close" onClick={() => setViewingTemplate(null)}>
            Close
          </Button>,
          <Button 
            key="edit" 
            type="primary" 
            onClick={() => {
              setViewingTemplate(null);
              handleEdit(viewingTemplate);
            }}
          >
            Edit Template
          </Button>,
        ]}
        width={700}
      >
        {viewingTemplate && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <strong>Template ID:</strong>
              <Tag color="blue" style={{ marginLeft: 8 }}>{viewingTemplate.templateId}</Tag>
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>Agent Name:</strong> {viewingTemplate.agentName}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>System Prompt:</strong>
              <div style={{ 
                marginTop: 8, 
                padding: 12, 
                background: '#f5f5f5', 
                borderRadius: 6,
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                fontSize: '13px'
              }}>
                {viewingTemplate.systemPrompt}
              </div>
            </div>
            {viewingTemplate.guardRailPrompt && (
              <div style={{ marginBottom: 16 }}>
                <strong>Guard Rail Prompt:</strong>
                <div style={{ 
                  marginTop: 8, 
                  padding: 12, 
                  background: '#fff7e6', 
                  borderRadius: 6,
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  border: '1px solid #ffd591'
                }}>
                  {viewingTemplate.guardRailPrompt}
                </div>
              </div>
            )}
            {viewingTemplate.handoffWebhookUrl && (
              <div style={{ marginBottom: 16 }}>
                <strong>Handoff Webhook:</strong>
                <div style={{ 
                  marginTop: 8, 
                  padding: 8, 
                  background: '#f9f9f9', 
                  borderRadius: 6,
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  border: '1px solid #eee'
                }}>
                  {viewingTemplate.handoffWebhookUrl}
                </div>
              </div>
            )}
            <div>
              <strong>Enabled Functions ({viewingTemplate.enabledFunctions?.length || 0}):</strong>
              <div style={{ marginTop: 8 }}>
                {viewingTemplate.enabledFunctions?.map(func => (
                  <Tag key={func} color="green" style={{ marginBottom: 4 }}>
                    {func}
                  </Tag>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TemplateManager;

